/**
 * Returns the date to use for both activationDate and nextInvoiceDate on load.
 * Uses the Next Invoice Date from Snowflake when present; otherwise defaults to today.
 */
export function resolveDefaultDate(
   nid: string | null | undefined,
   currentDate: string | null | undefined,
): string | null {
   return nid || (currentDate ?? null)
}

/**
 * Resolves the Next Invoice Date according to the quote's frozen status.
 *
 * Frozen (booked / closed-won / closed-lost):
 *   savedDbNid when present, otherwise activationDate.
 * Not frozen:
 *   snowflakeNid when present and not in the past (relative to currentDate), otherwise activationDate.
 *   savedDbNid is intentionally ignored — NID is not user-editable, so any stored value is just
 *   a stale derivation; snowflake is the live source of truth pre-freeze.
 */
export function resolveNextInvoiceDate({
   isFrozen,
   savedDbNid,
   snowflakeNid,
   activationDate,
   currentDate,
}: {
   isFrozen: boolean
   savedDbNid: string | null | undefined
   snowflakeNid: string | null | undefined
   activationDate: string | null
   currentDate?: string | null
}): string | null {
   const strip = (s: string | null | undefined): string | null =>
      s ? s.split('T')[0] : null

   if (isFrozen) {
      return strip(savedDbNid) ?? activationDate
   }

   const strippedNid = strip(snowflakeNid)
   const validNid =
      strippedNid !== null && currentDate != null && strippedNid < currentDate
         ? null
         : strippedNid
   // Fallback chain: live snowflake NID → activationDate → currentDate.
   // Returning null here strands the main render gate, so always prefer
   // currentDate over null when no other source is available.
   return (
      validNid ??
      activationDate ??
      (currentDate ? currentDate.split('T')[0] : null)
   )
}

/**
 * Resolves the initial activationDate and nextInvoiceDate state values on opportunity load.
 *
 * Activation date: saved DB value → cross-fill from saved NID → Snowflake NID → today.
 * Next invoice date: delegated to resolveNextInvoiceDate.
 */
export function resolveInitialDates(
   savedActivationDate: string | null | undefined,
   savedNextInvoiceDate: string | null | undefined,
   nid: string | null | undefined,
   currentDate: string | null | undefined,
   isFrozen: boolean = false,
): { activationDate: string | null; nextInvoiceDate: string | null } {
   const ad = savedActivationDate ? savedActivationDate.split('T')[0] : null
   const nidSaved = savedNextInvoiceDate
      ? savedNextInvoiceDate.split('T')[0]
      : null

   const activationDate: string | null =
      ad !== null || nidSaved !== null
         ? (ad ?? nidSaved)
         : resolveDefaultDate(nid, currentDate)

   const nextInvoiceDate = resolveNextInvoiceDate({
      isFrozen,
      savedDbNid: savedNextInvoiceDate,
      snowflakeNid: nid,
      activationDate,
      currentDate,
   })

   return { activationDate, nextInvoiceDate }
}
